import { Comment, User, UserState } from '@readcloud/data';
import C from 'classnames';
import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Button,
  ButtonWithConfirmation,
  Form,
  OverlayTrigger,
  TextArea,
  Tooltip,
} from '@readcloud/component-library';
import moment from 'moment';
import { MdDelete, MdEdit } from 'react-icons/md';

type Props = {
  comment: Comment;
  knownUsers: User[];
  user: UserState;
  UpdateComment: (updatedCommentText: string) => void;
  DeleteComment: (id: string) => void;
};

export const UserComment = (props: Props) => {
  const [commentAuthor, setCommentAuthor] = React.useState<{
    firstName: string;
    lastName: string;
    initials: string;
    isCurrentUser: boolean;
  }>();
  const [editing, setEditing] = React.useState<boolean>(false);
  const [updatedCommentText, setUpdatedCommentText] = React.useState<
    string | undefined
  >('');

  React.useEffect(() => {
    if (props.comment && props.knownUsers.length > 0) {
      // comment only stores an authorId, so finding user in knownUsers
      // and storing relevant data.
      const commentAuthor = props.knownUsers.find(
        (user) => user.id === props.comment.authorId
      );

      commentAuthor &&
        setCommentAuthor({
          firstName: commentAuthor.firstName,
          lastName: commentAuthor.lastName,
          initials: commentAuthor.firstName[0] + commentAuthor?.lastName[0],
          isCurrentUser: commentAuthor?.id === props.user?.id,
        });
    }
  }, [props.knownUsers.length, props.comment]);

  return (
    <div
      className={C(
        'rc-flex',
        commentAuthor?.isCurrentUser && 'rc-justify-end',
        'rc-py-2'
      )}
    >
      {commentAuthor?.isCurrentUser && (
        <OverlayTrigger
          placement={'bottom'}
          overlay={
            <Tooltip style={{ pointerEvents: 'auto' }}>
              {commentAuthor?.firstName + ' ' + commentAuthor?.lastName}
            </Tooltip>
          }
          popperConfig={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  altAxis: true, // false by default
                  mainAxis: true,
                  padding: 16,
                },
              },
            ],
          }}
        >
          <div className="rc-h-full">
            <Avatar
              size="sm"
              text={commentAuthor?.initials}
              circle
              textClassName="rc-bg-primary"
              className="rc-mr-2"
            />
          </div>
        </OverlayTrigger>
      )}

      <div
        className={C(
          'rc-flex rc-flex-col rc-flex-grow rc-flex-shrink rc-justify-center rc-p-2 rc-border-2 rc-rounded',
          commentAuthor?.isCurrentUser && 'rc-bg-primary/10'
        )}
      >
        {editing ? (
          <Form
            onSubmit={(e: React.FormEvent<Element>) => {
              e.preventDefault();
            }}
          >
            <TextArea
              name="add-comment"
              placeholder="Write a comment..."
              defaultValue={props.comment?.text}
              value={updatedCommentText}
              onChange={(e) => setUpdatedCommentText(e.currentTarget.value)}
            />
          </Form>
        ) : (
          <span>{props.comment?.text}</span>
        )}
        <div className="rc-py-2" />
        <div className="rc-flex rc-flex-row rc-justify-between rc-items-end">
          <small className="rc-text-gray-500">
            {moment
              .duration(moment(props.comment?.updated).diff(Date.now()))
              .humanize() + ' ago'}
          </small>
          {(commentAuthor?.isCurrentUser ||
            props.user?.role === 'Teacher' ||
            props.user?.role === 'Admin') && (
            <>
              <div className="rc-px-2" />
              {editing ? (
                <>
                  <div className="rc-flex">
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Cancel</Tooltip>}
                    >
                      <Button
                        size={'sm'}
                        variant="white"
                        onClick={(e) => {
                          e?.stopPropagation();
                          setEditing(false);
                        }}
                      >
                        <FontAwesomeIcon icon={'ban'} />
                      </Button>
                    </OverlayTrigger>
                    <div className="rc-px-1" />
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Submit</Tooltip>}
                    >
                      <Button
                        size={'sm'}
                        variant="white"
                        onClick={(e) => {
                          e?.stopPropagation();
                          props.UpdateComment(updatedCommentText || '');
                          setEditing(false);
                        }}
                      >
                        Submit
                      </Button>
                    </OverlayTrigger>
                  </div>
                </>
              ) : (
                <div className="rc-flex">
                  <OverlayTrigger
                    placement="bottom"
                    overlay={<Tooltip>Edit</Tooltip>}
                  >
                    <Button
                      size={'sm'}
                      variant="light"
                      onClick={(e) => {
                        e?.stopPropagation();
                        setUpdatedCommentText(props.comment?.text || '');
                        setEditing(true);
                      }}
                    >
                      <MdEdit size={24} />
                    </Button>
                  </OverlayTrigger>
                  <div className="rc-px-1" />

                  <ButtonWithConfirmation
                    confirmText="Delete"
                    confirmVariant="danger"
                    title="Delete Comment"
                    message="Are you sure you want to delete this comment?"
                    onToggleExtra={(e) => e?.stopPropagation()}
                    onConfirm={() =>
                      props.comment?.id && props.DeleteComment(props.comment.id)
                    }
                    renderButton={
                      <Button size={'sm'} variant="light">
                        <MdDelete size={24} />
                      </Button>
                    }
                    buttonTooltip="Delete"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {!commentAuthor?.isCurrentUser && (
        <OverlayTrigger
          placement={'bottom'}
          popperConfig={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  altAxis: true, // false by default
                  mainAxis: true,
                  padding: 16,
                },
              },
            ],
          }}
          overlay={
            <Tooltip style={{ pointerEvents: 'auto' }}>
              {commentAuthor?.firstName + ' ' + commentAuthor?.lastName}
            </Tooltip>
          }
        >
          <div className="rc-h-full">
            <Avatar
              size="sm"
              text={commentAuthor?.initials}
              circle
              textClassName="rc-bg-secondary"
              className="rc-ml-2"
            />
          </div>
        </OverlayTrigger>
      )}
    </div>
  );
};
